import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import type { CompanyFindManyQueryInterface } from '@zaoblako/shared/modules/client/dtos/company/company-find-many-query.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { HighlightMatchTextHelper } from '@zaoblako/web/shared/helpers/highlight-match-text.helper';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';
import { uniqBy } from 'lodash';
import { DropdownFilterEvent } from 'primeng/dropdown';

const DEFAULT_SELECT: CompanyFindManyQueryInterface['select'] = ['id', 'name', 'deleted'];

@Component({
  selector: 'crm-company-single-select',
  standalone: true,
  imports: [CommonModule, SingleSelectComponent, SafeHtmlPipe],
  templateUrl: './company-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanySingleSelectComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('singleSelectComponent') public singleSelectComponent: SingleSelectComponent;

  private readonly LIMIT = 20;
  private readonly FILTER_DEBOUNCE_MS = 300;

  @Input() public dropdownSelect: CompanyFindManyQueryInterface['select'] = [...DEFAULT_SELECT];

  @Input() public companies: CompanyFindInterface[] = [];

  @Input() public selectedCompany: CompanyFindInterface;
  @Output() public selectedCompanyChange = new EventEmitter<CompanyFindInterface>();

  @Input() public customRequest = false;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public showClear = false;
  @Input() public disabled = false;
  @Input() public isError = false;
  @Input() public appendTo: string;

  @Input() public excludeIds: number[] = [];

  public loading = false;

  private offset = 0;
  private search = '';
  private filterTimer?: ReturnType<typeof setTimeout>;

  public constructor(
    private readonly companyService: CompanyService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly globalService: GlobalService
  ) {}

  public ngOnInit(): void {
    if (!this.dropdownSelect?.length) {
      this.dropdownSelect = [...DEFAULT_SELECT];
    }

    if (this.shouldLoadFromApi()) {
      this.loadCompanies();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.excludeIds && this.shouldLoadFromApi()) {
      this.resetPaging();
      this.loadCompanies();
    }
  }

  public ngOnDestroy(): void {
    if (this.filterTimer) {
      clearTimeout(this.filterTimer);
    }
  }

  public onDropdownShow(): void {
    if (!this.shouldLoadFromApi()) {
      return;
    }

    this.resetPaging();
    this.loadCompanies();
  }

  public onDropdownFilter(event: DropdownFilterEvent): void {
    if (!this.shouldLoadFromApi()) {
      return;
    }

    if (this.filterTimer) {
      clearTimeout(this.filterTimer);
    }

    this.filterTimer = setTimeout(() => {
      this.search = event.filter ?? '';
      this.resetPaging();
      this.loadCompanies();
    }, this.FILTER_DEBOUNCE_MS);
  }

  public toCompany(company: CompanyFindInterface): CompanyFindInterface {
    return company;
  }

  public highlightMatchText(name: string | undefined): string {
    return (
      HighlightMatchTextHelper.highlightMatchText(name, this.singleSelectComponent?.dropdown?.filterValue) ||
      'Неизвестно'
    );
  }

  private shouldLoadFromApi(): boolean {
    return (
      !this.customRequest &&
      !!this.globalService.globalInfo.tenantInfo?.ratePlan?.modulesAccessControl?.BASIS?.enabled
    );
  }

  private resetPaging(): void {
    this.offset = 0;
  }

  private getQueryParams(): CompanyFindManyQueryInterface {
    const queryParams: CompanyFindManyQueryInterface = {
      limit: this.LIMIT,
      offset: this.offset,
      select: this.dropdownSelect
    };

    if (this.search?.length) {
      queryParams.search = this.search;
    }

    if (this.excludeIds?.length) {
      queryParams.excludeIds = this.excludeIds;
    } else if (this.selectedCompany?.id) {
      queryParams.excludeIds = [this.selectedCompany.id];
    }

    return queryParams;
  }

  private loadCompanies(): void {
    this.loading = true;
    this.cdRef.markForCheck();

    this.companyService.findMany(this.getQueryParams()).subscribe({
      next: (page) => {
        const merged = uniqBy(
          [...(this.selectedCompany ? [this.selectedCompany] : []), ...page.items],
          'id'
        ).filter((company) => !this.excludeIds.includes(company.id));

        this.companies = merged;
        this.offset = this.LIMIT;
        this.loading = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdRef.markForCheck();
      }
    });
  }
}
